/**
 * Transforms raw conversation history into a structured format
 * Each conversation consists of:
 * - A human message with a question
 * - An AI response with metadata
 * - Optional AI message with related questions
 */
export interface StructuredConversation {
  userQuestion: string;
  botResponse: string;
  relatedQuestions: string[];
  thread_Id: string;
  checkpoint_Id: string;
  liked: boolean | null;
  chart_data?: any; // Adding chart_data as an optional field
}

/**
 * Extracts questions from content containing <question> tags
 */
const extractQuestions = (content: string): string[] => {
  if (typeof content !== 'string') return [];
  
  const questions: string[] = [];
  const regex = /<question>(.*?)<\/question>/g;
  let match;
  
  while ((match = regex.exec(content)) !== null) {
    if (match[1] && match[1].trim()) {
      questions.push(match[1].trim());
    }
  }
  
  return questions;
};

/**
 * Transforms raw conversation history into structured conversations
 */
export const transformConversationHistory = (conversationHistory: any[]): StructuredConversation[] => {
  if (!Array.isArray(conversationHistory) || conversationHistory.length === 0) {
    return [];
  }
  
  const formattedConversations: StructuredConversation[] = [];
  let currentConversation: Partial<StructuredConversation> | null = null;
  
  // Process the conversation history
  for (let i = 0; i < conversationHistory.length; i++) {
    const message = conversationHistory[i];
    
    // Skip invalid messages
    if (!message || typeof message !== 'object' || !message.type) {
      continue;
    }
    
    // Handle human message - start of a conversation
    if (message.type === "human") {
      // Save any existing conversation in progress
      if (currentConversation?.userQuestion && currentConversation?.botResponse) {
        formattedConversations.push(currentConversation as StructuredConversation);
      }
      
      // Start a new conversation
      currentConversation = {
        userQuestion: message.content || "",
        botResponse: "",
        relatedQuestions: [],
        thread_Id: "",
        checkpoint_Id: "",
        liked: null,
        chart_data: undefined
      };
    }
    // Handle AI response message
    else if (message.type === "ai" && currentConversation) {
      const content = message.content || "";
      const hasQuestionTags = typeof content === 'string' && content.includes("<question>");
      
      // This is an AI message with questions
      if (hasQuestionTags) {
        // If this is the first AI message and it has both content and questions
        if (!currentConversation.botResponse) {
          // Split content: text before questions becomes the response
          const mainContent = content.split("<question>")[0].trim();
          currentConversation.botResponse = mainContent;
          currentConversation.thread_Id = message.threadId || "";
          currentConversation.checkpoint_Id = message.checkpointId || "";
          currentConversation.liked = message.liked !== undefined ? message.liked : null;
          currentConversation.chart_data = message.chart_data || undefined;
        }
        
        // Extract questions
        currentConversation.relatedQuestions = extractQuestions(content);
      }
      // This is a regular AI response message
      else if (!currentConversation.botResponse) {
        currentConversation.botResponse = content;
        currentConversation.thread_Id = message.threadId || "";
        currentConversation.checkpoint_Id = message.checkpointId || "";
        currentConversation.liked = message.liked !== undefined ? message.liked : null;
        currentConversation.chart_data = message.chart_data || undefined;
      }
    }
  }
  
  // Add the final conversation if still in progress
  if (currentConversation?.userQuestion && currentConversation?.botResponse) {
    formattedConversations.push(currentConversation as StructuredConversation);
  }
  
  return formattedConversations;
};
